import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Button,
  Stack,
} from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import { Edit, Delete } from '@mui/icons-material';
import { StoreData } from './stores.types';
import axios from 'axios';
import { API_URL } from '../../config/config';

const Store: React.FC = () => {
  const { user, isLoading } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [store, setStore] = useState<StoreData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [isConnectedToIg, setIsConnectedToIg] = useState(false);

  useEffect(() => {
    const fetchStore = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/store/${user?.id}/${id}`);
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || 'Failed to fetch store');
        }
        const data: any = await res.json();
        setStore(data.data);
        setIsConnectedToIg(!!data?.data?.ig_user_id);
      } catch (err: any) {
        setError(err.message || 'Unexpected error');
      } finally {
        setLoading(false);
      }
    };

    if (isLoading) return; // ← wait for auth to resolve
    if (!user) {
      navigate('/login'); // ← redirect if not logged in
      return;
    }
    if (id) fetchStore();
  }, [user, isLoading, id, navigate]);

  const goToInventory = (id: string) => {
    navigate('/inventory/' + id);
  };

  const goToUpdateStore = (id: string) => {
    navigate(`/update-store/${id}`);
  };

  const addInventory = () => {
    navigate('/create-inventory');
  };

  const deleteStore = async (id: string) => {
    const confirmed = window.confirm(
      'Do you really want to delete this store?',
    );

    if (confirmed && user) {
      try {
        await axios.delete(`${API_URL}/store/${user.id}/${id}`);
        // Optionally refresh the store list or give user feedback
        navigate('/stores');
      } catch (error) {
        console.error('Error deleting store:', error);
        alert('Something went wrong while deleting the store.');
      }
    } else {
    }
  };

  // frontend: call this when user clicks "Connect Instagram" on a store row
  function connectInstagram(storeId: string) {
    // redirect browser to backend login endpoint
    window.location.href = `https://api.qmagacin.store/api/ig/login?storeId=${encodeURIComponent(
      storeId,
    )}`;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 4 }}>
        navigate
        {error}
      </Alert>
    );
  }

  if (!store) {
    return (
      <Alert severity="info" sx={{ mt: 4 }}>
        No store found.
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, p: 2 }}>
      <Card sx={{ boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h4" gutterBottom>
            {store?.name}
          </Typography>
          <Typography variant="body1">
            <strong>Address:</strong> {store?.id_BusinessLocation?.address}
          </Typography>
          <Typography variant="body1">
            <strong>Phone:</strong> {store?.phone_number}
          </Typography>
          <Typography variant="body1">
            <strong>Revenue:</strong> {store?.revenue.toFixed(2)}
          </Typography>
          <Typography variant="body1">
            <strong>Email:</strong> {store?.email}
          </Typography>
          <Typography variant="body1">
            <strong>Status:</strong> {store?.status}
          </Typography>
          <Typography variant="body1">
            <strong>Capacity:</strong> {store?.capacity}
          </Typography>
          <Typography variant="body1">
            <strong>Region:</strong> {store?.region}, <strong>City:</strong>{' '}
            {store?.city}
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            <strong>Opening Hours:</strong>
          </Typography>
          <ul>
            {(() => {
              const hours =
                typeof store?.opening_hours === 'string'
                  ? JSON.parse(store?.opening_hours)
                  : store?.opening_hours;

              return (
                <>
                  <li>Mon-Fri: {hours?.['mon-fri'] ?? 'N/A'}</li>
                  <li>Sat: {hours?.sat ?? 'N/A'}</li>
                  <li>Sun: {hours?.sun ?? 'N/A'}</li>
                </>
              );
            })()}
          </ul>

          {store?.image_url && (
            <Box sx={{ mt: 2 }}>
              <img
                src={store?.image_url}
                alt={store?.name}
                style={{ maxWidth: '100%', borderRadius: 8 }}
              />
            </Box>
          )}

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Edit />}
              onClick={() => goToUpdateStore(store?.id)}
            >
              Update
            </Button>

            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={() => deleteStore(store?.id)}
            >
              Delete
            </Button>
            {!isConnectedToIg ? (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<InstagramIcon />}
                disabled
                onClick={() => connectInstagram(store?.id)}
              >
                Connect to Instagram
              </Button>
            ) : (
              ''
            )}
          </Stack>
        </CardContent>
      </Card>

      <Box sx={{ p: 2 }}>
        {store?.StoreInventories && store?.StoreInventories.length > 0 ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h4" gutterBottom>
                Inventories
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Button
                  onClick={() => addInventory()}
                  variant="contained"
                  sx={{
                    minWidth: 0,
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    padding: 0,
                    fontSize: '1.5rem',
                    lineHeight: 1,
                    textAlign: 'center',
                  }}
                >
                  +
                </Button>
              </Box>
            </div>

            {store?.StoreInventories.map(inv => (
              <Card
                onClick={() => goToInventory(inv.id)}
                key={inv.id}
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  boxShadow: 3,
                  '&:hover': { boxShadow: 6 },
                  cursor: 'pointer',
                }}
              >
                <CardContent>
                  <Typography variant="h6" color="primary">
                    {inv.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Quantity: {inv.quantity}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h4" gutterBottom>
              No Inventories
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Button
                onClick={() => addInventory()}
                variant="contained"
                sx={{
                  minWidth: 0,
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  padding: 0,
                  fontSize: '1.5rem',
                  lineHeight: 1,
                  textAlign: 'center',
                }}
              >
                +
              </Button>
            </Box>
          </div>
        )}
      </Box>
    </Box>
  );
};

export default Store;
